import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from "react";
import { Button } from "../../../components/common";
import {
  useAppDispatch,
  useAppSelector,
  getChatInboxThunk,
  getChatMessagesThunk,
  sendChatMessageThunk,
  setActiveOtherPartyId,
  getStudentsThunk,
} from "../../../redux";
import { showSuccess, showError } from "../../../services";
import { formatChatMessageTime } from "../../../utils";
import refreshIcon from "../../../assets/icons/refresh.svg";
import broadcastIcon from "../../../assets/icons/broadcast.svg";
import megaphoneIcon from "../../../assets/icons/megaphone.svg";
import "./TeacherInbox.css";

const TeacherInbox: React.FC = () => {
  const dispatch = useAppDispatch();
  const chatContainerRef = useRef<HTMLDivElement>(null);

  const initialScrolledPartiesRef = useRef<Record<string, boolean>>({});
  const shouldScrollToBottomRef = useRef<boolean>(false);
  const prevScrollHeightRef = useRef<number>(0);
  const prevScrollTopRef = useRef<number>(0);
  const isPrependingRef = useRef<boolean>(false);

  const { user } = useAppSelector((state) => state.auth);
  const { records: assignedStudents, isLoading: isStudentsLoading } = useAppSelector(
    (state) => state.student
  );
  const {
    conversations,
    activeOtherPartyId,
    messagesByParty,
    hasMoreByParty,
    isFetchingOlderByParty,
    isLoading: isChatLoading,
  } = useAppSelector((state) => state.chat);

  const [messageInput, setMessageInput] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [isMultiSelect, setIsMultiSelect] = useState<boolean>(false);
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);

  useEffect(() => {
    dispatch(getChatInboxThunk({ force: true }));
    if (user?.id) {
      dispatch(getStudentsThunk(user.id));
    }
  }, [dispatch, user?.id]);

  const assignedThreads = useMemo(() => {
    const threads = assignedStudents.map((student) => {
      const fullName = [student.First_Name, student.Middle_Name, student.Last_Name]
        .filter(Boolean)
        .join(" ");
      const displayName = student.Student_Number2
        ? `${student.Student_Number2} - ${fullName}`
        : fullName;
      const matchingConv = conversations.find((c) => c.otherPartyId === student.id);
      return {
        otherPartyId: student.id,
        otherPartyName: fullName,
        displayName,
        email: student.Email,
        studentNumber: student.Student_Number2,
        lastMessage: matchingConv?.lastMessage || "",
        lastMessageAt: matchingConv?.lastMessageAt || "",
        unreadCount: matchingConv?.unreadCount || 0,
      };
    });

    return threads.sort((a, b) => {
      const timeA = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : 0;
      const timeB = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : 0;
      return timeB - timeA;
    });
  }, [assignedStudents, conversations]);

  const filteredThreads = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return assignedThreads;
    return assignedThreads.filter(
      (item) =>
        item.displayName.toLowerCase().includes(q) ||
        item.email.toLowerCase().includes(q)
    );
  }, [assignedThreads, searchQuery]);

  useEffect(() => {
    if (!isMultiSelect && assignedThreads.length > 0) {
      const exists = assignedThreads.some((t) => t.otherPartyId === activeOtherPartyId);
      if (!activeOtherPartyId || !exists) {
        dispatch(setActiveOtherPartyId(assignedThreads[0].otherPartyId));
      }
    }
  }, [assignedThreads, activeOtherPartyId, isMultiSelect, dispatch]);

  const activeThread = assignedThreads.find(
    (t) => t.otherPartyId === activeOtherPartyId
  );
  const activeMessages = activeOtherPartyId
    ? messagesByParty[activeOtherPartyId] || []
    : [];
  const isFetchingOlder = activeOtherPartyId
    ? !!isFetchingOlderByParty[activeOtherPartyId]
    : false;
  const hasMore = activeOtherPartyId
    ? hasMoreByParty[activeOtherPartyId] !== false
    : true;
  const isLoading = isChatLoading || isStudentsLoading;

  const handleRefresh = () => {
    if (user?.id) {
      dispatch(getStudentsThunk({ teacherId: user.id, force: true }));
    }
    dispatch(getChatInboxThunk({ force: true }));
    if (activeOtherPartyId) {
      initialScrolledPartiesRef.current[activeOtherPartyId] = false;
      dispatch(
        getChatMessagesThunk({ recipientId: activeOtherPartyId, force: true })
      );
    }
  };

  useEffect(() => {
    if (!isMultiSelect && activeOtherPartyId) {
      dispatch(getChatMessagesThunk({ recipientId: activeOtherPartyId, force: true }));
    }
  }, [dispatch, activeOtherPartyId, isMultiSelect]);

  useLayoutEffect(() => {
    const container = chatContainerRef.current;
    if (!container || !activeOtherPartyId || isMultiSelect) return;

    if (isPrependingRef.current) {
      container.scrollTop =
        prevScrollTopRef.current +
        (container.scrollHeight - prevScrollHeightRef.current);
      isPrependingRef.current = false;
    } else if (
      shouldScrollToBottomRef.current ||
      (!initialScrolledPartiesRef.current[activeOtherPartyId] &&
        activeMessages.length > 0)
    ) {
      container.scrollTop = container.scrollHeight;
      requestAnimationFrame(() => {
        container.scrollTop = container.scrollHeight;
      });
      initialScrolledPartiesRef.current[activeOtherPartyId] = true;
      shouldScrollToBottomRef.current = false;
    }
  }, [activeMessages, activeOtherPartyId, activeThread, isMultiSelect]);

  const handleScroll = () => {
    const container = chatContainerRef.current;
    if (!container || !activeOtherPartyId || isMultiSelect) return;

    if (
      container.scrollTop <= 20 &&
      hasMore &&
      !isFetchingOlder &&
      !isLoading
    ) {
      prevScrollHeightRef.current = container.scrollHeight;
      prevScrollTopRef.current = container.scrollTop;
      isPrependingRef.current = true;

      dispatch(
        getChatMessagesThunk({
          recipientId: activeOtherPartyId,
          offset: activeMessages.length,
          limit: 10,
          isOlder: true,
        })
      );
    }
  };

  const toggleMultiSelect = () => {
    setIsMultiSelect((prev) => {
      const next = !prev;
      if (next && activeOtherPartyId) {
        setSelectedStudentIds([activeOtherPartyId]);
      } else {
        setSelectedStudentIds([]);
      }
      return next;
    });
  };

  const toggleStudentSelection = (studentId: string) => {
    setSelectedStudentIds((prev) =>
      prev.includes(studentId)
        ? prev.filter((id) => id !== studentId)
        : [...prev, studentId]
    );
  };

  const handleSelectAllStudents = () => {
    const allIds = filteredThreads.map((t) => t.otherPartyId);
    setSelectedStudentIds(allIds);
  };

  const handleClearStudentSelection = () => {
    setSelectedStudentIds([]);
  };

  const handleSelectConversation = (otherPartyId: string) => {
    if (isMultiSelect) {
      toggleStudentSelection(otherPartyId);
    } else {
      dispatch(setActiveOtherPartyId(otherPartyId));
      setMessageInput("");
    }
  };

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = messageInput.trim();

    if (!trimmed) {
      showError("Please enter a message to send.");
      return;
    }

    if (isMultiSelect) {
      if (selectedStudentIds.length === 0) {
        showError("Please select at least one student.");
        return;
      }

      shouldScrollToBottomRef.current = true;
      dispatch(
        sendChatMessageThunk({
          recipientIds: selectedStudentIds,
          message: trimmed,
        })
      ).then((res) => {
        if (sendChatMessageThunk.fulfilled.match(res)) {
          showSuccess(
            `Message sent successfully to ${selectedStudentIds.length} student${selectedStudentIds.length === 1 ? "" : "s"}!`
          );
          setMessageInput("");
          setSelectedStudentIds([]);
          setIsMultiSelect(false);
        }
      });
    } else {
      if (!activeOtherPartyId) return;

      shouldScrollToBottomRef.current = true;
      dispatch(
        sendChatMessageThunk({
          recipientId: activeOtherPartyId,
          message: trimmed,
        })
      ).then((res) => {
        if (sendChatMessageThunk.fulfilled.match(res)) {
          showSuccess("Message sent successfully!");
          setMessageInput("");
        }
      });
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <div className="chat-container-card">
        {/* Sidebar Conversation List */}
        <div className="chat-sidebar">
          <div className="chat-sidebar-header">
            <h3>Conversations</h3>
            <div className="chat-sidebar-actions">
              <button
                type="button"
                className={`chat-multi-btn ${isMultiSelect ? "active" : ""}`}
                onClick={toggleMultiSelect}
                title={isMultiSelect ? "Cancel multi-select broadcast" : "Select multiple students to broadcast"}
                aria-label={isMultiSelect ? "Cancel broadcast" : "Broadcast message"}
              >
                <img
                  src={broadcastIcon}
                  alt="Broadcast"
                  className="chat-broadcast-icon"
                />
              </button>
              <button
                type="button"
                className="chat-refresh-btn"
                onClick={handleRefresh}
                title="Refresh conversations"
                aria-label="Refresh conversations"
              >
                <img
                  src={refreshIcon}
                  alt="Refresh"
                  className={`chat-refresh-icon ${isLoading ? "spinning" : ""}`}
                />
              </button>
            </div>
          </div>

          <div className="chat-search-wrapper">
            <input
              type="text"
              placeholder="Search assigned students..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="chat-search-input"
            />
          </div>

          {isMultiSelect && (
            <div className="chat-multi-toolbar">
              <span>Selected: {selectedStudentIds.length}</span>
              <div>
                <button type="button" onClick={handleSelectAllStudents}>
                  Select All
                </button>
                {" | "}
                <button type="button" onClick={handleClearStudentSelection}>
                  Clear
                </button>
              </div>
            </div>
          )}

          <div className="chat-threads-list">
            {assignedThreads.length === 0 ? (
              <div style={{ padding: "20px", textAlign: "center", color: "#8c8594" }}>
                {isLoading ? "Loading assigned students..." : "No assigned students found"}
              </div>
            ) : filteredThreads.length === 0 ? (
              <div style={{ padding: "20px", textAlign: "center", color: "#8c8594" }}>
                No matching students found
              </div>
            ) : (
              filteredThreads.map((item) => {
                const isActive = !isMultiSelect && item.otherPartyId === activeOtherPartyId;
                const isChecked = selectedStudentIds.includes(item.otherPartyId);
                return (
                  <div
                    key={item.otherPartyId}
                    className={`chat-thread-item ${isActive ? "active" : ""}`}
                    onClick={() => handleSelectConversation(item.otherPartyId)}
                  >
                    {isMultiSelect && (
                      <input
                        type="checkbox"
                        className="chat-checkbox"
                        checked={isChecked}
                        onChange={() => toggleStudentSelection(item.otherPartyId)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    )}
                    <div className="chat-avatar">
                      {item.otherPartyName.charAt(0).toUpperCase()}
                    </div>
                    <div className="chat-thread-info">
                      <div className="chat-thread-top">
                        <span className="chat-participant-name">{item.displayName}</span>
                        <span className="chat-last-time">
                          {formatChatMessageTime(item.lastMessageAt, "list")}
                        </span>
                      </div>
                      <div className="chat-thread-bottom">
                        <span className="chat-last-msg">{item.lastMessage}</span>
                        {item.unreadCount > 0 && (
                          <span className="chat-unread-badge">{item.unreadCount}</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Main Chat Conversation View */}
        {isMultiSelect ? (
          <div className="chat-main">
            <div className="chat-main-header">
              <div className="chat-avatar" style={{ backgroundColor: "#663a93" }}>
                <img src={megaphoneIcon} alt="Broadcast" style={{ width: "20px", height: "20px", display: "block" }} />
              </div>
              <div className="chat-header-info">
                <div className="chat-header-title">Broadcast Message</div>
                <span className="chat-header-role">
                  {selectedStudentIds.length} Student{selectedStudentIds.length === 1 ? "" : "s"} Selected
                </span>
              </div>
            </div>

            <div className="chat-selected-chips-container">
              <div className="chat-selected-chips-title">
                Recipients ({selectedStudentIds.length}):
              </div>
              {selectedStudentIds.length === 0 ? (
                <span style={{ fontSize: "0.8rem", color: "#8c8594" }}>
                  No students selected. Check boxes in the left list to select recipients.
                </span>
              ) : (
                selectedStudentIds.map((id) => {
                  const studentObj = assignedThreads.find((t) => t.otherPartyId === id);
                  const name = studentObj ? studentObj.displayName : id;
                  return (
                    <span key={id} className="chat-student-chip">
                      {name}
                      <button
                        type="button"
                        className="chat-student-chip-remove"
                        onClick={() => toggleStudentSelection(id)}
                        title="Remove student"
                      >
                        ×
                      </button>
                    </span>
                  );
                })
              )}
            </div>

            <div className="empty-chat-placeholder">
              {selectedStudentIds.length > 0
                ? `Compose your message below to send it to all ${selectedStudentIds.length} selected students at once.`
                : "Select students from the left list to broadcast a message."}
            </div>

            <form onSubmit={handleSendMessage} className="chat-input-area">
              <input
                type="text"
                placeholder={
                  selectedStudentIds.length > 0
                    ? `Type message to broadcast to ${selectedStudentIds.length} student(s)...`
                    : "Select students to send a message..."
                }
                value={messageInput}
                onChange={(e) => setMessageInput(e.target.value)}
                className="chat-input-field"
                disabled={selectedStudentIds.length === 0}
              />
              <Button
                type="submit"
                variant="primary"
                size="medium"
                disabled={selectedStudentIds.length === 0}
              >
                Send Broadcast
              </Button>
            </form>
          </div>
        ) : activeThread ? (
          <div className="chat-main">
            <div className="chat-main-header">
              <div className="chat-avatar">
                {activeThread.otherPartyName.charAt(0).toUpperCase()}
              </div>
              <div className="chat-header-info">
                <div className="chat-header-title">{activeThread.displayName}</div>
                <span className="chat-header-role">Student</span>
              </div>
            </div>

            <div
              className="chat-messages-body"
              ref={chatContainerRef}
              onScroll={handleScroll}
            >
              {isFetchingOlder && (
                <div className="chat-loading-older">Loading older messages...</div>
              )}
              {activeMessages.length === 0 ? (
                <div className="empty-chat-placeholder">
                  No messages yet. Send a message to start!
                </div>
              ) : (
                activeMessages.map((msg) => {
                  const isSentByTeacher = msg.Sender_Type === "Teacher";
                  return (
                    <div
                      key={msg.id}
                      className={`chat-msg-bubble ${isSentByTeacher ? "sent" : "received"}`}
                    >
                      <div>{msg.Message}</div>
                      <div className="chat-msg-time">
                        {formatChatMessageTime(msg.Created_Time, "bubble")}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Single WhatsApp-style Chat Input Bar */}
            <form onSubmit={handleSendMessage} className="chat-input-area">
              <input
                type="text"
                placeholder="Type a message..."
                value={messageInput}
                onChange={(e) => setMessageInput(e.target.value)}
                className="chat-input-field"
              />
              <Button type="submit" variant="primary" size="medium">
                Send
              </Button>
            </form>
          </div>
        ) : (
          <div className="empty-chat-placeholder">Select a student to start chatting</div>
        )}
      </div>
    </div>
  );
};

export default TeacherInbox;
